import { X, FileText, Check, Plus, Trash2, Pencil, Calendar, AlertCircle } from 'lucide-react'
import { formatDue } from '../../lib/dueDate'
import Linkify from '../ui/Linkify'
import DangerDialog from '../ui/DangerDialog'
import CardDateHistory from '../CardDateHistory'
import AttachmentLightbox from '../AttachmentLightbox'
import FilePreviewModal from '../ui/FilePreviewModal'
import CompletionKpiModal from '../CompletionKpiModal'
import CardActionsPanel from './CardActionsPanel'
import CardAttachmentsTab from './CardAttachmentsTab'
import CardChecklistTab from './CardChecklistTab'
import CardCommentsTab from './CardCommentsTab'
import CardDetailTabs from './CardDetailTabs'
import CardDependenciesTab from './CardDependenciesTab'
import CardMembersPanel from './CardMembersPanel'
import CardProcedureLinkPanel from './CardProcedureLinkPanel'
import CardTicketDetailsPanel from './CardTicketDetailsPanel'
import { isImageAttachment } from './cardDetailUtils'

export default function CardDetailView(props) {
    const {
        t, card, readOnly, onClose, updateError, isOverdue, effectiveIsCompleted, labels, editingTitle, title, setTitle, handleSaveTitle, setEditingTitle, cardDescription, editingDesc, setEditingDesc, desc, setDesc, handleSaveDesc, canEditDescription, canClaimPoolTask, handleClaimPoolTask, claimingPoolTask, poolClaimedByMe, canClaimProcedure, handleClaimProcedure, joiningProcedure, canLeaveProcedure, handleLeaveProcedure, activeTab, setActiveTab, totalItems, doneItems, attachments, dependencies, comments, progressPct, procedureChecklists, manualChecklists, checklists, addingChecklist, setAddingChecklist, newChecklistName, setNewChecklistName, handleAddChecklist, addingChecklistLoading, procedureByChecklistId, user, canCreate, canContribute, isAdmin, canManageCardMembers, canDeleteCard, members, companyMembers, editingItemId, editingItemText, setEditingItemId, setEditingItemText, updateItem, onRefreshCards, handleToggleItem, stepDoneQty, stepBusyId, qtyPopoverId, qtyDraft, setQtyDraft, setQtyPopoverId, handleStepToggle, handleStepQuantity, expandedSharedItem, setExpandedSharedItem, sharedItemSearch, setSharedItemSearch, sharedItemPicker, setSharedItemPicker, toggleShared, updateSharedMembers, deleteItem, newItemTexts, setNewItemTexts, handleAddItem, addingItemLoading, setConfirmDeleteChecklist, fileInputRef, handleFileUpload, uploadingFile, moveAttachment, setConfirmRemoveAttachment, procedureFiles, openLightbox, setPreviewFile, addingDependency, setAddingDependency, dependType, setDependType, dependTargetId, setDependTargetId, allCards, addingDepLoading, handleAddDependencyLink, removeDependency, profile, commentReactions, commentImageInputRef, handleCommentImageUpload, commentText, setCommentText, submittingComment, handleAddComment, commentError, editingCommentId, editingCommentText, setEditingCommentId, setEditingCommentText, updateComment, removeComment, showAchievement, handleToggleCommentReaction, replyingTo, setReplyingTo, replyText, setReplyText, replySubmitting, replyImageInputRef, handleAddReply, cardProcedures, procedures, linkingProcedure, setLinkingProcedure, linkingSchedule, setLinkingSchedule, linkingProcLoading, setLinkingProcLoading, linkProcedure, fetchChecklists, getProcedureChecklistStats, fieldStatus, renderFieldState, runFieldUpdate, configLocked, epics, completionFields, effectiveTimerStartedAt, doneForCurrentUser, procedureIndexMap, timeLogs, groupedTimeLogs, activeTimer, loadingAverage, averageMinutes, linkedProcedureId, availableMembers, addingMember, setAddingMember, handleAddMember, setConfirmRemoveMember, canWorkOnCard, cardActionPending, handleStartFromDetail, handleCompleteFromDetail, normalizedCompletionPayload, selectedKpiUserId, setSelectedKpiUserId, displayCompletionKpis, handleToggleCardShared, sharedCardPicker, setSharedCardPicker, handleConfirmCardShared, handleUpdateCardSharedMembers, setConfirmDelete, confirmDelete, onDelete, confirmRemoveAttachment, removeAttachment, lightboxIndex, lightboxImages, setLightboxIndex, closeLightbox, previewFile, confirmRemoveMember, handleRemoveMember, confirmDeleteChecklist, removeChecklist, showCompletionModal, myCompletionKpis, completionModalSaving, setShowCompletionModal, setCompletionModalSaving, setUpdateError, finalizeCardCompletion
    } = props

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className={`card-detail-modal ${readOnly ? 'card-detail-readonly' : ''}`.trim()} onClick={e => e.stopPropagation()}>
                <div className="card-detail-top-actions">
                    <button className="detail-panel-close" onClick={onClose}><X size={18} /></button>
                </div>

                {readOnly && (
                    <div className="card-detail-readonly-banner" role="status">
                        {t('card.readOnly.banner', { defaultValue: 'Mod spectator — doar citire. Vezi cardul, nu îl poți modifica.' })}
                    </div>
                )}

                {/* Title row — click anywhere on the title to rename */}
                <div className="card-detail-title-row">
                    {editingTitle ? (
                        <input
                            className="inline-input card-detail-title-input"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            onBlur={handleSaveTitle}
                            onKeyDown={e => {
                                if (e.key === 'Enter') handleSaveTitle()
                                if (e.key === 'Escape') { setTitle(card.title); setEditingTitle(false) }
                            }}
                            autoFocus
                        />
                    ) : (
                        <h2
                            className={`card-detail-title ${readOnly ? '' : 'card-detail-title-editable'} ${effectiveIsCompleted ? 'completed-text' : ''}`.trim()}
                            onClick={() => { if (!readOnly) setEditingTitle(true) }}
                            role={readOnly ? undefined : 'button'}
                            tabIndex={readOnly ? undefined : 0}
                            onKeyDown={e => { if (!readOnly && (e.key === 'Enter' || e.key === ' ')) setEditingTitle(true) }}
                            title={readOnly ? undefined : t('card.title.editTooltip')}
                        >
                            {card.title}
                            <Pencil size={13} className="card-detail-title-pencil" />
                        </h2>
                    )}
                </div>

                <div className="card-detail-body">
                    <div className="card-detail-main">

                        {updateError && (
                            <div className="date-error cd-error-block">
                                <AlertCircle size={14} /> {updateError}
                            </div>
                        )}
                        {/* Meta info row */}
                        <div className="card-meta-row">
                            {card.due_date && (
                                <div className={`card-meta-chip ${isOverdue ? 'overdue' : ''} ${effectiveIsCompleted ? 'done-chip' : ''}`}>
                                    <Calendar size={13} />
                                    {formatDue(card.due_date, 'dd MMM yyyy')}
                                    {isOverdue && !effectiveIsCompleted && <span className="overdue-badge">{t('card.meta.overdue')}</span>}
                                    {effectiveIsCompleted && <span className="done-badge">{t('card.meta.done')}</span>}
                                </div>
                            )}
                            {labels.map(l => (
                                <span key={l.id} className="card-label-tag" style={{ background: l.color }}>{l.name}</span>
                            ))}
                        </div>

                        {/* Description */}
                        <div className="card-detail-section">
                            <div className="card-detail-section-title">
                                <FileText size={16} /> {t('card.description.heading')}
                                <div className="cd-section-actions">
                                    {canClaimPoolTask && (
                                        <button
                                            type="button"
                                            className="cd-claim-btn"
                                            onClick={handleClaimPoolTask}
                                            disabled={claimingPoolTask}
                                        >
                                            <Check size={13} /> {claimingPoolTask ? '...' : t('dashboard.actions.claim')}
                                        </button>
                                    )}
                                    {poolClaimedByMe && !canClaimPoolTask && (
                                        <span className="cd-claimed-pill">{t('dashboard.task.claimedByMe')}</span>
                                    )}
                                    {canClaimProcedure && (
                                        <button
                                            type="button"
                                            className="cd-claim-btn"
                                            onClick={handleClaimProcedure}
                                            disabled={joiningProcedure}
                                            title={t('card.procedure.claimHint')}
                                        >
                                            <Check size={13} /> {joiningProcedure ? '...' : t('card.procedure.claim')}
                                        </button>
                                    )}
                                    {canLeaveProcedure && (
                                        <button
                                            type="button"
                                            className="edit-btn"
                                            onClick={handleLeaveProcedure}
                                            disabled={joiningProcedure}
                                        >
                                            {joiningProcedure ? '...' : t('card.procedure.leave')}
                                        </button>
                                    )}
                                    {canEditDescription && !editingDesc && (
                                        <button className="edit-btn" onClick={() => setEditingDesc(true)}>{t('card.description.edit')}</button>
                                    )}
                                </div>
                            </div>
                            {editingDesc ? (
                                <div>
                                    <textarea
                                        className="card-detail-textarea"
                                        value={desc}
                                        onChange={e => setDesc(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Escape') { setDesc(cardDescription || ''); setEditingDesc(false) }
                                        }}
                                        placeholder={t('card.description.placeholder')}
                                        rows={4}
                                        autoFocus
                                    />
                                    <div className="cd-inline-actions">
                                        <button className="btn btn-primary btn-sm" onClick={handleSaveDesc}>{t('common.save')}</button>
                                        <button className="btn btn-secondary btn-sm" onClick={() => { setEditingDesc(false); setDesc(cardDescription || '') }}>{t('common.cancel')}</button>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className={`card-detail-description ${canEditDescription ? 'clickable' : ''}`}
                                    onClick={() => canEditDescription && setEditingDesc(true)}
                                >
                                    {cardDescription ? <Linkify text={cardDescription} /> : <span className="placeholder-text">{canEditDescription ? t('card.description.empty.admin') : t('card.description.empty.viewer')}</span>}
                                </div>
                            )}
                        </div>

                        <CardDetailTabs
                            t={t}
                            activeTab={activeTab}
                            onChange={setActiveTab}
                            totalItems={totalItems}
                            doneItems={doneItems}
                            attachmentsCount={attachments.length}
                            dependenciesCount={dependencies.length}
                            commentsCount={comments.length}
                        />

                        {/* CHECKLIST TAB */}
                        {activeTab === 'checklist' && (
                            <CardChecklistTab
                                t={t}
                                totalItems={totalItems}
                                progressPct={progressPct}
                                procedureChecklists={procedureChecklists}
                                manualChecklists={manualChecklists}
                                checklists={checklists}
                                addingChecklist={addingChecklist}
                                setAddingChecklist={setAddingChecklist}
                                newChecklistName={newChecklistName}
                                setNewChecklistName={setNewChecklistName}
                                handleAddChecklist={handleAddChecklist}
                                addingChecklistLoading={addingChecklistLoading}
                                canEditDescription={canEditDescription}
                                procedureByChecklistId={procedureByChecklistId}
                                userId={user?.id}
                                readOnly={readOnly}
                                canCreate={canCreate}
                                canContribute={canContribute}
                                isAdmin={isAdmin}
                                members={members}
                                companyMembers={companyMembers}
                                editingItemId={editingItemId}
                                editingItemText={editingItemText}
                                setEditingItemId={setEditingItemId}
                                setEditingItemText={setEditingItemText}
                                updateItem={updateItem}
                                onRefreshCards={onRefreshCards}
                                handleToggleItem={handleToggleItem}
                                stepDoneQty={stepDoneQty}
                                stepBusyId={stepBusyId}
                                qtyPopoverId={qtyPopoverId}
                                qtyDraft={qtyDraft}
                                setQtyDraft={setQtyDraft}
                                setQtyPopoverId={setQtyPopoverId}
                                handleStepToggle={handleStepToggle}
                                handleStepQuantity={handleStepQuantity}
                                expandedSharedItem={expandedSharedItem}
                                setExpandedSharedItem={setExpandedSharedItem}
                                sharedItemSearch={sharedItemSearch}
                                setSharedItemSearch={setSharedItemSearch}
                                sharedItemPicker={sharedItemPicker}
                                setSharedItemPicker={setSharedItemPicker}
                                toggleShared={toggleShared}
                                updateSharedMembers={updateSharedMembers}
                                deleteItem={deleteItem}
                                newItemTexts={newItemTexts}
                                setNewItemTexts={setNewItemTexts}
                                handleAddItem={handleAddItem}
                                addingItemLoading={addingItemLoading}
                                setConfirmDeleteChecklist={setConfirmDeleteChecklist}
                            />
                        )}

                        {/* ATTACHMENTS TAB */}
                        {activeTab === 'attachments' && (
                            <CardAttachmentsTab
                                t={t}
                                user={user}
                                canContribute={canContribute}
                                fileInputRef={fileInputRef}
                                onFileUpload={handleFileUpload}
                                uploadingFile={uploadingFile}
                                attachments={attachments}
                                isAdmin={isAdmin}
                                moveAttachment={moveAttachment}
                                onRemoveAttachmentRequest={setConfirmRemoveAttachment}
                                procedureFiles={procedureFiles}
                                openLightbox={openLightbox}
                                setPreviewFile={setPreviewFile}
                            />
                        )}

                        {/* DEPENDENCIES TAB */}
                        {activeTab === 'dependencies' && (
                            <CardDependenciesTab
                                t={t}
                                canCreate={canCreate}
                                addingDependency={addingDependency}
                                setAddingDependency={setAddingDependency}
                                dependType={dependType}
                                setDependType={setDependType}
                                dependTargetId={dependTargetId}
                                setDependTargetId={setDependTargetId}
                                allCards={allCards}
                                cardId={card.id}
                                addingDepLoading={addingDepLoading}
                                onAddDependency={handleAddDependencyLink}
                                dependencies={dependencies}
                                isAdmin={isAdmin}
                                removeDependency={removeDependency}
                            />
                        )}


                        {/* COMMENTS TAB */}
                        {activeTab === 'comments' && (
                            <CardCommentsTab
                                t={t}
                                profile={profile}
                                comments={comments}
                                attachments={attachments}
                                commentReactions={commentReactions}
                                currentUserId={user?.id}
                                commentImageInputRef={commentImageInputRef}
                                handleCommentImageUpload={handleCommentImageUpload}
                                commentText={commentText}
                                setCommentText={setCommentText}
                                companyMembers={companyMembers}
                                submittingComment={submittingComment}
                                uploadingFile={uploadingFile}
                                handleAddComment={handleAddComment}
                                commentError={commentError}
                                editingCommentId={editingCommentId}
                                editingCommentText={editingCommentText}
                                setEditingCommentId={setEditingCommentId}
                                setEditingCommentText={setEditingCommentText}
                                updateComment={updateComment}
                                removeComment={removeComment}
                                showAchievement={showAchievement}
                                openLightbox={openLightbox}
                                setPreviewFile={setPreviewFile}
                                handleToggleCommentReaction={handleToggleCommentReaction}
                                replyingTo={replyingTo}
                                setReplyingTo={setReplyingTo}
                                replyText={replyText}
                                setReplyText={setReplyText}
                                replySubmitting={replySubmitting}
                                replyImageInputRef={replyImageInputRef}
                                handleAddReply={handleAddReply}
                            />
                        )}

                        {activeTab === 'checklist' && (
                            <CardProcedureLinkPanel
                                t={t}
                                cardProcedures={cardProcedures}
                                procedures={procedures}
                                isAdmin={isAdmin}
                                linkingProcedure={linkingProcedure}
                                setLinkingProcedure={setLinkingProcedure}
                                linkingSchedule={linkingSchedule}
                                setLinkingSchedule={setLinkingSchedule}
                                linkingProcLoading={linkingProcLoading}
                                setLinkingProcLoading={setLinkingProcLoading}
                                linkProcedure={linkProcedure}
                                userId={user?.id}
                                fetchChecklists={fetchChecklists}
                                showAchievement={showAchievement}
                                getProcedureChecklistStats={getProcedureChecklistStats}
                            />
                        )}

                        {/* UNIFIED HISTORY TAB — date history */}
                        {activeTab === 'history' && (
                            <div className="cd-history-unified">
                                <CardDateHistory
                                    cardId={card.id}
                                    userId={user?.id}
                                    isAdmin={isAdmin}
                                />
                            </div>
                        )}

                    </div>

                    {/* Right sidebar actions */}
                    <div className="card-detail-sidebar">
                        <CardTicketDetailsPanel
                            t={t}
                            card={card}
                            companyMembers={companyMembers}
                            fieldStatus={fieldStatus}
                            renderFieldState={renderFieldState}
                            runFieldUpdate={runFieldUpdate}
                            isAdmin={isAdmin}
                            configLocked={configLocked}
                            epics={epics}
                            completionFields={completionFields}
                            readOnly={readOnly}
                            effectiveTimerStartedAt={effectiveTimerStartedAt}
                            doneForCurrentUser={doneForCurrentUser}
                            procedures={procedures}
                            procedureIndexMap={procedureIndexMap}
                            timeLogs={timeLogs}
                            groupedTimeLogs={groupedTimeLogs}
                            activeTimer={activeTimer}
                            effectiveIsCompleted={effectiveIsCompleted}
                            userId={user?.id}
                            loadingAverage={loadingAverage}
                            averageMinutes={averageMinutes}
                            linkedProcedureId={linkedProcedureId}
                        />

                        <CardMembersPanel
                            t={t}
                            members={members}
                            canManageMembers={canManageCardMembers}
                            isAdmin={isAdmin}
                            readOnly={readOnly}
                            availableMembers={availableMembers}
                            addingMember={addingMember}
                            onStartAddMember={() => setAddingMember(true)}
                            onCancelAddMember={() => setAddingMember(false)}
                            onAddMember={handleAddMember}
                            onRemoveMemberRequest={setConfirmRemoveMember}
                        />

                        {(canWorkOnCard || completionFields.length > 0 || canCreate || card.is_shared || isAdmin || canDeleteCard) && (
                            <CardActionsPanel
                                t={t}
                                card={card}
                                canWorkOnCard={canWorkOnCard}
                                doneForCurrentUser={doneForCurrentUser}
                                effectiveTimerStartedAt={effectiveTimerStartedAt}
                                cardActionPending={cardActionPending}
                                onStart={handleStartFromDetail}
                                onComplete={handleCompleteFromDetail}
                                completionFields={completionFields}
                                normalizedCompletionPayload={normalizedCompletionPayload}
                                members={members}
                                selectedKpiUserId={selectedKpiUserId}
                                onSelectKpiUser={setSelectedKpiUserId}
                                displayCompletionKpis={displayCompletionKpis}
                                canCreate={canCreate}
                                onToggleCardShared={handleToggleCardShared}
                                sharedCardPicker={sharedCardPicker}
                                onOpenSharedCardPicker={() => setSharedCardPicker(true)}
                                companyMembers={companyMembers}
                                onConfirmCardShared={handleConfirmCardShared}
                                onUpdateCardSharedMembers={handleUpdateCardSharedMembers}
                                onCancelSharedCardPicker={() => setSharedCardPicker(false)}
                                isAdmin={isAdmin}
                                canDeleteCard={canDeleteCard}
                                onDeleteRequest={() => setConfirmDelete(true)}
                            />
                        )}
                    </div>
                </div>
            </div>
            <DangerDialog
                open={confirmDelete}
                title={t('card.deleteCard.title', { title: card.title })}
                description={t('card.deleteCard.description')}
                confirmLabel={t('card.deleteCard.confirm')}
                onClose={() => setConfirmDelete(false)}
                onConfirm={async () => {
                    await onDelete(card.id)
                    setConfirmDelete(false)
                    onClose()
                }}
            />
            <DangerDialog
                open={Boolean(confirmRemoveAttachment)}
                title={t('card.deleteAttachment.title', { name: confirmRemoveAttachment?.name || '' })}
                description={t('card.deleteAttachment.description')}
                confirmLabel={t('card.deleteAttachment.confirm')}
                onClose={() => setConfirmRemoveAttachment(null)}
                onConfirm={async () => {
                    const att = confirmRemoveAttachment
                    setConfirmRemoveAttachment(null)
                    await removeAttachment(att.id, att.url)
                }}
            />
            {lightboxIndex !== null && (() => {
                const imgs = lightboxImages || attachments.filter(isImageAttachment)
                if (!imgs.length || lightboxIndex < 0 || lightboxIndex >= imgs.length) return null
                return (
                    <AttachmentLightbox
                        images={imgs}
                        index={lightboxIndex}
                        currentUserId={user?.id}
                        onNavigate={setLightboxIndex}
                        onClose={closeLightbox}
                    />
                )
            })()}
            {previewFile && (
                <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
            )}
            <DangerDialog
                open={Boolean(confirmRemoveMember)}
                title={t('card.removeMember.title', { name: confirmRemoveMember?.full_name || confirmRemoveMember?.email || '' })}
                description={t('card.removeMember.description')}
                confirmLabel={t('card.removeMember.confirm')}
                onClose={() => setConfirmRemoveMember(null)}
                onConfirm={() => handleRemoveMember(confirmRemoveMember.id)}
            />
            <DangerDialog
                open={Boolean(confirmDeleteChecklist)}
                title={t('card.deleteChecklist.title', { name: confirmDeleteChecklist?.name || '' })}
                description={t('card.deleteChecklist.description')}
                confirmLabel={t('card.deleteChecklist.confirm')}
                onClose={() => setConfirmDeleteChecklist(null)}
                onConfirm={async () => {
                    const cl = confirmDeleteChecklist
                    setConfirmDeleteChecklist(null)
                    await removeChecklist(cl.id)
                    onRefreshCards()
                }}
            />
            {showCompletionModal ? (
                <CompletionKpiModal
                    fields={completionFields}
                    initialValues={myCompletionKpis}
                    saving={completionModalSaving}
                    onClose={() => {
                        if (completionModalSaving) return
                        setShowCompletionModal(false)
                    }}
                    onSubmit={async (values) => {
                        setCompletionModalSaving(true)
                        setUpdateError('')
                        try {
                            const result = await finalizeCardCompletion({ completionValues: values })
                            if (result?.success !== false) {
                                setShowCompletionModal(false)
                            }
                        } finally {
                            setCompletionModalSaving(false)
                        }
                    }}
                />
            ) : null}
        </div>
    )
}
